import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
} from '../../components/DataTablePanel';
import { FilterField } from '../../components/ListFiltersBar';
import { MydataPageShell } from './MydataPageShell';
import { TableIconButton } from '../../components/TableIconAction';
import {
  Badge,
  Callout,
  ListEmptyState,
  ListPagination,
  LoadingSpinner,
  PdfIcon,
  SpinnerIcon,
  TextInput,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type { MydataDocumentListItem } from '../../types/mydata';
import { formatDate, formatMoney } from '../../utils/format';
import { MydataYearFilterBar } from './MydataYearFilterBar';

const DOC_PAGE_SIZE_OPTIONS = [20, 50, 100];

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    from: `${year}-${pad2(month)}-01`,
    to: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
}

function counterpartyLabel(doc: MydataDocumentListItem): string {
  if (doc.direction === 'transmitted') {
    return doc.counterpartName || doc.counterpartVat || t().mydata.emptyValue;
  }
  return doc.issuerName || doc.issuerVat || t().mydata.emptyValue;
}

export function MydataDocumentsListPage() {
  const labels = t().mydata;
  const monthNames = labels.monthNamesShort;
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const { from: dateFrom, to: dateTo } = useMemo(
    () => monthRange(year, month),
    [year, month],
  );
  const [direction, setDirection] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [docsMeta, setDocsMeta] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    pageCount: 1,
  });
  const [documents, setDocuments] = useState<MydataDocumentListItem[]>([]);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfMark, setPdfMark] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfInfo, setPdfInfo] = useState<string | null>(null);

  const downloadPdf = useCallback(
    async (doc: MydataDocumentListItem) => {
      if (!doc.hasOfficialPdf) return;
      setPdfMark(doc.mark);
      setPdfError(null);
      setPdfInfo(null);
      // Open immediately so popup blockers allow HTML/provider pages after fetch.
      const previewWindow = window.open('about:blank', '_blank');
      try {
        const series = doc.series ?? 'doc';
        const aa = doc.aa ?? doc.mark;
        const result = await api.downloadMydataDocumentPdf(
          doc.mark,
          `mydata-${series}-${aa}-${doc.mark}.pdf`,
          { previewWindow },
        );
        if (result.mode === 'html-viewer') {
          setPdfInfo(labels.pdfHtmlViewerOpened);
        }
      } catch (err) {
        previewWindow?.close();
        setPdfError(
          err instanceof ApiError ? err.message : labels.pdfUnavailable,
        );
      } finally {
        setPdfMark(null);
      }
    },
    [labels.pdfHtmlViewerOpened, labels.pdfUnavailable],
  );

  const changeYear = useCallback((y: number) => {
    setYear(y);
    setPage(1);
  }, []);

  const changeMonth = useCallback((m: number) => {
    setMonth(m);
    setPage(1);
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      const next = searchInput.trim();
      setSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const fetchDocuments = useCallback(async () => {
    return api.getMydataDocuments({
      dateFrom,
      dateTo,
      direction:
        direction === 'all'
          ? undefined
          : (direction as 'transmitted' | 'received'),
      search: search.trim() || undefined,
      page,
      pageSize,
    });
  }, [dateFrom, dateTo, direction, search, page, pageSize]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [connection, docs] = await Promise.all([
          api.getMydataConnection(),
          fetchDocuments(),
        ]);
        if (cancelled) return;
        setConfigured(Boolean(connection.configured));
        setDocuments(docs.data);
        setDocsMeta(docs.meta);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
          setDocuments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchDocuments]);

  return (
    <MydataPageShell>
      <div className="shrink-0 space-y-1">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {labels.documentsHint}
        </p>
        <div className="text-xs text-slate-500">
          <Link to="/mydata" className="text-brand hover:underline">
            {labels.periodTitle}
          </Link>
          {' · '}
          <Link to="/mydata/sync" className="text-brand hover:underline">
            {labels.syncPageLink}
          </Link>
          {' · '}
          <Link to="/mydata/pdf-export" className="text-brand hover:underline">
            {t().nav.mydataPdfExport}
          </Link>
          {' · '}
          <Link to="/mydata/debts" className="text-brand hover:underline">
            {labels.sectionDebts}
          </Link>
          {' · '}
          <Link
            to="/settings/mydata-connection"
            className="text-brand hover:underline"
          >
            {labels.connectionTitle}
          </Link>
        </div>
      </div>

      {!configured ? (
        <Callout variant="warning" className="shrink-0">
          {labels.notConfigured}{' '}
          <Link to="/settings/mydata-connection" className="underline">
            {labels.connectionTitle}
          </Link>
        </Callout>
      ) : null}

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {pdfError ? (
        <Callout variant="danger" className="shrink-0">
          {pdfError}
        </Callout>
      ) : null}

      {pdfInfo ? (
        <Callout variant="info" className="shrink-0">
          {pdfInfo}
        </Callout>
      ) : null}

      <MydataYearFilterBar year={year} onYearChange={changeYear}>
        <FilterField label={labels.searchDocuments} wide>
          <TextInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={labels.searchDocuments}
          />
        </FilterField>
      </MydataYearFilterBar>

      <div className="shrink-0">
        <div className="mb-1.5 text-xs font-medium text-slate-500">
          {labels.month}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
            const selected = month === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => changeMonth(m)}
                className={[
                  'rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                  selected
                    ? 'border-brand bg-brand-soft text-brand'
                    : 'border-line text-ink-muted hover:border-brand/30',
                ].join(' ')}
              >
                {monthNames[m - 1] ?? m}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {labels.documents}
          {docsMeta.total > 0 ? ` (${docsMeta.total})` : ''}
        </h2>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-700">
          {(
            [
              { value: 'all', label: labels.filterAll },
              { value: 'transmitted', label: labels.filterIncome },
              { value: 'received', label: labels.filterExpense },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setDirection(opt.value);
                setPage(1);
              }}
              className={[
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                direction === opt.value
                  ? 'bg-brand text-white'
                  : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : documents.length === 0 ? (
        <ListEmptyState
          title={labels.noDocuments}
          hint={labels.emptySyncHint}
        />
      ) : (
        <>
          <DataTablePanel className="min-w-0">
            <table className="min-w-full text-left text-sm">
              <thead className={dataTableHeadClass}>
                <tr>
                  <th className="px-3 py-2">{labels.issueDate}</th>
                  <th className="px-3 py-2">{labels.mark}</th>
                  <th className="px-3 py-2">{labels.invoiceType}</th>
                  <th className="px-3 py-2">{labels.direction}</th>
                  <th className="px-3 py-2">{labels.counterparty}</th>
                  <th className="px-3 py-2 text-right">{labels.net}</th>
                  <th className="px-3 py-2 text-right">{labels.vat}</th>
                  <th className="px-3 py-2 text-right">{labels.gross}</th>
                  <th className="px-3 py-2 w-12 text-right">
                    <span className="sr-only">{labels.downloadPdf}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc.mark}
                    className="border-t border-slate-100 dark:border-slate-800"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDate(doc.issueDate)}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/mydata/documents/${encodeURIComponent(doc.mark)}`}
                        className="font-medium text-brand hover:underline"
                      >
                        {doc.mark}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{doc.invoiceType || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          doc.direction === 'transmitted'
                            ? 'success'
                            : 'secondary'
                        }
                      >
                        {doc.direction === 'transmitted'
                          ? labels.directionTransmitted
                          : labels.directionReceived}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 max-w-[14rem] truncate">
                      {counterpartyLabel(doc)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(doc.netValue)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(doc.vatAmount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {formatMoney(doc.grossValue)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {doc.hasOfficialPdf ? (
                        <TableIconButton
                          label={
                            pdfMark === doc.mark
                              ? labels.downloadingPdf
                              : labels.downloadPdf
                          }
                          disabled={pdfMark != null}
                          onClick={() => void downloadPdf(doc)}
                        >
                          {pdfMark === doc.mark ? (
                            <SpinnerIcon className="h-4 w-4" />
                          ) : (
                            <PdfIcon className="h-4 w-4" />
                          )}
                        </TableIconButton>
                      ) : (
                        <span className="text-xs text-ink-faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTablePanel>
          <ListPagination
            page={docsMeta.page}
            totalPages={docsMeta.pageCount}
            total={docsMeta.total}
            itemsPerPage={pageSize}
            pageSizeOptions={DOC_PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </>
      )}
    </MydataPageShell>
  );
}
